import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

interface AffiliateProgram {
  id: string;
  program_name: string;
  description?: string;
  default_commission_rate: number;
  commission_type: string;
  cookie_duration_days: number;
  minimum_payout: number;
  payment_terms: string;
}

interface PublicSignupForm {
  id: string;
  program_id: string;
  form_title?: string;
  form_description?: string;
  logo_url?: string;
  primary_color?: string;
  success_message?: string;
  terms_and_conditions?: string;
  privacy_policy_url?: string;
  redirect_url?: string;
  is_active: boolean;
}

const AffiliateSignup: React.FC = () => {
  const { formId, programId } = useParams();
  const signupFormId = (formId as string | undefined) || (programId as string | undefined);
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<AffiliateProgram | null>(null);
  const [signupForm, setSignupForm] = useState<PublicSignupForm | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    website: '',
    companyName: '',
    phoneNumber: '',
    marketingPlans: '',
    acceptTerms: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        setLoading(true);
        if (!signupFormId) {
          throw new Error('Missing signup form id');
        }

        const baseUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.trim() : '';
        const response = await fetch(`${baseUrl}/api/affiliate/signup/${encodeURIComponent(signupFormId)}`);
        
        if (!response.ok) {
          throw new Error('Program not found or no longer available');
        }
        
        const data = await response.json();
        setProgram(data.program);
        setSignupForm(data.form);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching program details:', error);
        toast.error('Failed to load program details. The program may not exist or is no longer available.');
        setLoading(false);
      }
    };
    
    fetchProgramDetails();
  }, [signupFormId]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.acceptTerms) {
      toast.error('You must accept the terms and conditions to proceed');
      return;
    }
    
    try {
      setSubmitting(true);

      if (!signupFormId) {
        throw new Error('Missing signup form id');
      }

      const baseUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.trim() : '';
      const response = await fetch(`${baseUrl}/api/affiliate/signup/${encodeURIComponent(signupFormId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_name: formData.companyName,
          contact_name: formData.fullName,
          contact_email: formData.email,
          website_url: formData.website,
          why_join: formData.marketingPlans,
          phone: formData.phoneNumber,
          agreed_to_terms: formData.acceptTerms
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit application');
      }
      
      const data = await response.json();
      
      setSuccess(true);
      toast.success(data?.message || 'Your application has been submitted successfully!');

      if (data?.redirect_url) {
        try {
          window.location.href = data.redirect_url;
        } catch {
          // ignore
        }
      }
    } catch (error: any) {
      console.error('Error submitting application:', error);
      toast.error(error.message || 'Failed to submit your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
            <p className="mt-2 text-center text-sm text-gray-600">Loading program details...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!program) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <h2 className="text-center text-2xl font-extrabold text-gray-900">Program Not Found</h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              The affiliate program you're looking for could not be found. Please check the URL and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Application Submitted!</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Thank you for applying to the {program.program_name}.
          </p>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-900">Application successful!</h3>
              <p className="mt-2 text-sm text-gray-500">
                We've received your application and will review it shortly. You'll receive an email with further instructions.
              </p>
              <div className="mt-5">
                <a
                  href="/"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Return to Home
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">{signupForm?.form_title || program.program_name}</h2>
        <p className="mt-2 text-center text-sm text-gray-600 max-w">
          {signupForm?.form_description || program.description || 'Join our affiliate program and start earning today!'}
        </p>
      </div>
      
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="mb-6 p-4 bg-blue-50 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1 md:flex md:justify-between">
                <p className="text-sm text-blue-700">
                  Commission rate: {program.default_commission_rate}{program.commission_type === 'percentage' ? '%' : '£'} | 
                  Cookie duration: {program.cookie_duration_days} days | 
                  Min payout: £{program.minimum_payout} | 
                  Payments: {program.payment_terms}
                </p>
              </div>
            </div>
          </div>
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Full Name *</label>
              <div className="mt-1">
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address *</label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700">Website/Blog URL *</label>
              <div className="mt-1">
                <input
                  id="website"
                  name="website"
                  type="url"
                  required
                  value={formData.website}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="https://"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">Company Name</label>
              <div className="mt-1">
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number</label>
              <div className="mt-1">
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="marketingPlans" className="block text-sm font-medium text-gray-700">
                How do you plan to market our products? *
              </label>
              <div className="mt-1">
                <textarea
                  id="marketingPlans"
                  name="marketingPlans"
                  rows={4}
                  required
                  value={formData.marketingPlans}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Please describe how you plan to promote our products..."
                />
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                id="acceptTerms"
                name="acceptTerms"
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={handleCheckboxChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-900">
                I agree to the <a href="https://explanatum.co.uk/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-500">Terms and Conditions</a> *
              </label>
            </div>
            
            <div>
              <button
                type="submit"
                disabled={submitting}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  submitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AffiliateSignup;
